/**
 * Service Act editor (2026-06-03).
 * Спрощена версія GoodsSaleEditor — без складу, без серій.
 * Тільки номенклатура з is_service=true.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceActs, type ServiceActItem } from '@/api/serviceActs';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';
import { ProjectField } from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';
import VatFlagsButton from '@/shared/components/VatFlagsButton';
import {
  DEFAULT_VAT_FLAGS, lineAmounts, orgVatDefaults, recalcRows, type DocVatFlags, vatPayerOn,
} from '@/shared/utils/vatFlags';


interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  advance_offset_mode: 'AUTO' | 'NONE';
  project_id: string | null;
  service_period_from: string;
  service_period_to: string;
  /** ч.1374: ознаки ПДВ документа як у 1С (умовчання — з картки організації). */
  vat_flags: DocVatFlags;
  comment: string;
  items: ServiceActItem[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  advance_offset_mode: 'AUTO',
  project_id: null,
  service_period_from: '',
  service_period_to: '',
  vat_flags: { ...DEFAULT_VAT_FLAGS },
  comment: '',
  items: [],
};

const newRow = (
  n: number, vatId: string, taxPurposeId: string | null,
): ServiceActItem => ({
  line_number: n,
  nomenclature_id: '',
  description: '',
  quantity: '1',
  price: '0',
  amount_without_vat: '0',
  vat_rate_id: vatId,
  vat_amount: '0',
  total_amount: '0',
  tax_purpose_id: taxPurposeId,
  accounting_account_code: '703',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function ServiceActEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('sales.service_act.manage');

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const buyers = useMemo(
    () => contractorsQ.data?.items.filter((c) => c.is_buyer) ?? [],
    [contractorsQ.data],
  );

  // ⚠ Тільки is_service=true.
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const serviceNoms = useMemo(
    () => nomListQ.data?.items.filter((n) => n.is_service && !n.deletion_mark) ?? [],
    [nomListQ.data],
  );

  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const taxPurposesList = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });

  const docQuery = useQuery({
    queryKey: ['service-act', id],
    queryFn: () => serviceActs.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'service-act', (cid) => serviceActs.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);
  const defaultTaxPurposeId = useMemo(
    () => taxPurposesList.data?.find((tp) => tp.code === 'TAXABLE')?.id ?? null,
    [taxPurposesList.data],
  );

  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  // ч.1374: колонки ПДВ — платник І «враховувати ПДВ» у документі.
  // ч.1383: платник ПДВ — на дату документа; збережений документ з ПДВ лишається з ПДВ,
  // навіть якщо організація потім перестала бути платником.
  const docVatPayer = vatPayerOn(currentOrg, form.document_date)
    || (!isNew && form.vat_flags.vat_considered);
  const showVat = docVatPayer && form.vat_flags.vat_considered;
  const rateOf = (vatRateId: string | null | undefined) =>
    vatList.data?.find((v) => v.id === vatRateId)?.rate_percent ?? 0;
  const vatFlagsTouched = useRef(false);
  useEffect(() => {
    if (!isNew || copy.source || vatFlagsTouched.current || !currentOrg) return;
    const next = orgVatDefaults(currentOrg, form.document_date);
    setForm((f) => (f.vat_flags.vat_considered === next.vat_considered
      && f.vat_flags.prices_include_vat === next.prices_include_vat && f.vat_flags.vat_auto === next.vat_auto)
      ? f : { ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, copy.source, currentOrg, vatList.data, form.document_date]);
  function applyVatFlags(next: DocVatFlags) {
    vatFlagsTouched.current = true;
    setForm((f) => ({ ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf) }));
  }
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveVatId = showVat ? defaultVatId : exemptVatId;

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1, effectiveVatId, defaultTaxPurposeId)],
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id ?? '',
        advance_offset_mode: d.advance_offset_mode ?? 'AUTO',
        project_id: d.project_id ?? null,
        service_period_from: d.service_period_from ?? '',
        service_period_to: d.service_period_to ?? '',
        vat_flags: {
          vat_considered: d.vat_considered ?? true,
          prices_include_vat: d.prices_include_vat,
          vat_auto: d.vat_auto ?? true,
        },
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          description: it.description,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          tax_purpose_id: it.tax_purpose_id ?? null,
          accounting_account_code: it.accounting_account_code ?? '703',
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate() }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, effectiveVatId, defaultTaxPurposeId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  function setItem(i: number, patch: Partial<ServiceActItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // Recalc (ч.1374: за ознаками документа — ціни з ПДВ / без, ПДВ вручну).
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch || 'vat_amount' in patch) {
          const a = lineAmounts(Number(next.quantity || 0), Number(next.price || 0), rateOf(next.vat_rate_id),
            f.vat_flags, { manualVat: 'vat_amount' in patch ? Number(next.vat_amount) : null });
          next.amount_without_vat = a.amount_without_vat;
          next.vat_amount = a.vat_amount;
          next.total_amount = a.total_amount;
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, newRow(f.items.length + 1, effectiveVatId, defaultTaxPurposeId)],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const create = useMutation({
    mutationFn: () => serviceActs.create({
      organization_id: form.organization_id,
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      advance_offset_mode: form.advance_offset_mode,
      project_id: form.project_id,
      service_period_from: form.service_period_from || null,
      service_period_to: form.service_period_to || null,
      ...form.vat_flags,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['service-acts'] });
      navigate(`/service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => serviceActs.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      advance_offset_mode: form.advance_offset_mode,
      project_id: form.project_id,
      service_period_from: form.service_period_from || null,
      service_period_to: form.service_period_to || null,
      ...form.vat_flags,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-act', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await serviceActs.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        advance_offset_mode: form.advance_offset_mode,
        project_id: form.project_id,
        service_period_from: form.service_period_from || null,
        service_period_to: form.service_period_to || null,
        ...form.vat_flags,
        comment: form.comment || null,
        items: form.items,
      });
      try {
        await serviceActs.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/service-acts/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['service-acts'] });
      navigate(`/service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await serviceActs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        advance_offset_mode: form.advance_offset_mode,
        project_id: form.project_id,
        service_period_from: form.service_period_from || null,
        service_period_to: form.service_period_to || null,
        ...form.vat_flags,
        comment: form.comment || null,
        items: form.items,
      });
      await serviceActs.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-act', id] });
      qc.invalidateQueries({ queryKey: ['service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => serviceActs.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-act', id] });
      qc.invalidateQueries({ queryKey: ['service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => serviceActs.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-acts'] });
      navigate('/service-acts');
    },
  });

  // ч.860: прогрес — ЛИШЕ обовʼязкові поля.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Замовник і договір', required: 1,
      filled: form.contractor_id ? 1 : 0 },
    { id: 'period', title: 'Період і напрям', required: 0, filled: 0 },
    { id: 'items', title: 'Послуги', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.860: редактор був поза каноном (свої кнопки з емодзі, без
          вкладень/дт-кт) — переведено на DocumentToolbar (Закони #6/#7)
          + новий дизайн (Закон #10). «Повʼязаних дій» у акта немає —
          картка у рейці не показується. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий акт послуг' : `Акт послуг: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            railMode
            isNew={isNew}
            isPosted={isPosted}
            onBackToList={() => navigate('/service-acts')}
            onSave={() => (isNew ? create.mutate() : update.mutate())}
            saveDisabled={isPosted || create.isPending || update.isPending || createAndPost.isPending}
            savePending={create.isPending || update.isPending}
            onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
            postDisabled={form.items.length === 0 || !form.organization_id || !form.contractor_id}
            postPending={createAndPost.isPending || updateAndPost.isPending}
            postTitle="Зберігає та проводить документ"
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            onPrint={() => navigate(`/service-acts/${id}/print`)}
            entriesDoc={{ type: 'doc.service_acts', id: isNew ? '' : id! }}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} isNew={isNew}
          actions={[copyRailAction(isNew, () => navigate(`/service-acts/new?copy=${id}`))]}
          doc={{ type: 'doc.service_acts', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew || isPosted}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted}
                 placeholder={isNew ? 'АП-0001-2026 (auto)' : undefined} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Замовник і договір">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Замовник *</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {buyers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Договір{' '}
            <small style={{ color: 'var(--muted)' }}>(авто вкл.)</small>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">— без договору —</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ч.1193: залік авансу замовника — як на Реалізації. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isPosted ? 'default' : 'pointer', margin: '0.5rem 0 0' }}>
        <input type="checkbox"
          checked={form.advance_offset_mode === 'NONE'}
          disabled={isPosted}
          onChange={(e) => setForm({ ...form, advance_offset_mode: e.target.checked ? 'NONE' : 'AUTO' })} />
        <span>не зараховувати аванс автоматично
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
            (типово аванс за договором зараховується при проведенні)
          </span>
        </span>
      </label>
      </DocBlock>

      <DocBlock index={3} title="Період і напрям">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Період надання послуг з</label>
          <DatePopover value={form.service_period_from}
                 onChange={(v) => setForm({ ...form, service_period_from: v })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>По</label>
          <DatePopover value={form.service_period_to}
                 onChange={(v) => setForm({ ...form, service_period_to: v })}
                 disabled={isPosted} />
        </div>
        <ProjectField span={4} label="Проект / напрям"
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={4} title="Послуги"
        actions={!isPosted ? (<>
          <VatFlagsButton value={form.vat_flags} onChange={applyVatFlags}
            isVatPayer={docVatPayer} />
          <button className="primary" onClick={addRow}>+ додати рядок</button>
        </>) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Послуга</th>
            <th>Опис</th>
            <th style={{ width: 90 }}>К-ть</th>
            <th style={{ width: 110 }}>{form.vat_flags.prices_include_vat ? 'Ціна з ПДВ' : 'Ціна'}</th>
            <th style={{ width: 130 }}>Сума</th>
            {showVat && (
              <>
                <th style={{ width: 100 }}>ПДВ %</th>
                <th style={{ width: 110 }}>ПДВ</th>
                <th style={{ width: 130 }}>Всього</th>
              </>
            )}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  kind="services"
                  onChange={(v) => setItem(i, { nomenclature_id: v })}
                />
                {serviceNoms.length === 0 && (
                  <small style={{ color: '#dc2626' }}>
                    Немає послуг у довіднику. Створіть номенклатуру з ✓ is_service.
                  </small>
                )}
              </td>
              <td>
                <input value={it.description ?? ''}
                       onChange={(e) => setItem(i, { description: e.target.value })}
                       disabled={isPosted}
                       placeholder="Опис послуги (необов'язково)" />
              </td>
              <td>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItem(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td>
                <input type="number" step="0.01" value={it.price}
                       onChange={(e) => setItem(i, { price: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ textAlign: 'right' }}>
                {fmt(Number(it.amount_without_vat))}
              </td>
              {showVat && (
                <>
                  <td>
                    <select value={it.vat_rate_id}
                            onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                            disabled={isPosted}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!form.vat_flags.vat_auto && !isPosted ? (
                      <input type="number" step="0.01" min="0" value={it.vat_amount}
                             title="Сума ПДВ вручну (авторозрахунок ПДВ вимкнено)"
                             onChange={(e) => setItem(i, { vat_amount: e.target.value })} />
                    ) : fmt(Number(it.vat_amount))}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {fmt(Number(it.total_amount))}
                  </td>
                </>
              )}
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {showVat && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* ч.860: вкладення — на всіх документах (рішення власника 2026-08-13);
              у акта їх раніше не було. */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.service_acts" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error">{error}</div>}
    </div>
  );
}


function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: { data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> } }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
